import { useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, Upload, CheckCircle2, Loader2, FileText } from "lucide-react";
import { submitLead, uploadBill, trackEvent } from "@/lib/api";
import { PHOTOS, JEREMY } from "@/lib/jeremy";

const UTILITIES = [
  { value: "dominion", label: "Dominion Energy Virginia" },
  { value: "rec", label: "Rappahannock Electric Cooperative" },
  { value: "other", label: "Other" },
];
const BILL_BUCKETS = ["Under $100", "$100–$150", "$150–$250", "$250–$400", "$400+"];
const ROOF_MATERIALS = ["Asphalt shingle", "Metal", "Tile", "Slate", "Other"];
const ROOF_AGES = ["Under 5 years", "5–10 years", "10–15 years", "15+ years"];

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const phoneRegex = /^[\d\s()+\-]{10,}$/;

const initial = {
  full_name: "",
  mobile_phone: "",
  email: "",
  property_address: "",
  utility_provider: "",
  monthly_bill: "",
  owns_home: null,
  roof_material: "",
  roof_age: "",
  has_hoa: null,
  moving_soon: null,
  preferred_datetime: "",
  bill_upload_filename: null,
  consent_contact: false,
};

export default function QualificationFunnel() {
  const [params] = useSearchParams();
  const startOnUpload = params.get("upload") === "1";
  const [step, setStep] = useState(1);
  const [data, setData] = useState(initial);
  const [errors, setErrors] = useState({});
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [started, setStarted] = useState(false);
  const navigate = useNavigate();
  const fileRef = useRef(null);

  const TOTAL = 5;

  const set = (k, v) => {
    setData((d) => ({ ...d, [k]: v }));
    setErrors((e) => ({ ...e, [k]: undefined }));
    if (!started) { setStarted(true); trackEvent("form_start"); }
  };

  const validateStep = () => {
    const e = {};
    if (step === 1) {
      if (!data.full_name.trim()) e.full_name = "Required";
      if (!phoneRegex.test(data.mobile_phone)) e.mobile_phone = "Enter a valid phone";
      if (!emailRegex.test(data.email)) e.email = "Enter a valid email";
      if (!data.property_address.trim()) e.property_address = "Required";
    }
    if (step === 2) {
      if (!data.utility_provider) e.utility_provider = "Choose your utility";
      if (!data.monthly_bill) e.monthly_bill = "Pick a range";
      if (data.owns_home === null) e.owns_home = "Please answer";
    }
    if (step === 3) {
      if (!data.roof_material) e.roof_material = "Choose one";
      if (!data.roof_age) e.roof_age = "Choose one";
      if (data.has_hoa === null) e.has_hoa = "Please answer";
      if (data.moving_soon === null) e.moving_soon = "Please answer";
    }
    if (step === 5) {
      if (!data.consent_contact) e.consent_contact = "Consent is required to submit";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const next = () => {
    if (!validateStep()) return;
    setStep((s) => Math.min(TOTAL, s + 1));
  };
  const back = () => setStep((s) => Math.max(1, s - 1));

  const handleFile = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const res = await uploadBill(file);
      set("bill_upload_filename", res.filename);
      trackEvent("bill_upload", { filename: res.filename, size: res.size });
      toast.success("Utility bill uploaded", { description: file.name });
    } catch (err) {
      toast.error("Upload failed", { description: err?.response?.data?.detail || "Try a smaller file (<8MB)." });
    } finally {
      setUploading(false);
    }
  };

  const submit = async () => {
    if (!validateStep()) return;
    setSubmitting(true);
    try {
      await submitLead({ ...data });
      trackEvent("form_complete", { utility: data.utility_provider });
      if (data.preferred_datetime) trackEvent("scheduled_consultation", { when: data.preferred_datetime });
      navigate("/thank-you", { state: { name: data.full_name, when: data.preferred_datetime } });
    } catch (err) {
      toast.error("Submission failed", { description: err?.response?.data?.detail || "Please try again." });
    } finally {
      setSubmitting(false);
    }
  };

  const progress = (step / TOTAL) * 100;

  return (
    <section className="pt-28 md:pt-36 pb-20 bg-[#FDFBF7] min-h-screen" data-testid="qualification-funnel">
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        <div className="flex items-start gap-4 md:gap-5 max-w-2xl">
          <div className="w-14 h-14 md:w-16 md:h-16 rounded-full overflow-hidden border-2 border-[#D97706] shrink-0" data-testid="funnel-jeremy-avatar">
            <img src={PHOTOS.hero} alt="Jeremy" className="w-full h-full object-cover" />
          </div>
          <div>
            <div className="eyebrow">A conversation with Jeremy</div>
            <h1 className="mt-2 font-serif-display text-3xl md:text-5xl font-semibold text-[#0F382C] leading-[1.1]">
              Let's find out if solar makes sense for your home.
            </h1>
            <p className="mt-4 text-slate-600">
              I'll use this information to understand your property and electric usage. Completing this form does not obligate you to purchase anything.
            </p>
            <p className="mt-2 text-xs text-slate-400">
              No Social Security number required. Any soft credit check happens later — through a compliant third party, with your explicit authorization.
            </p>
          </div>
        </div>

        <div className="mt-8 mb-6">
          <div className="flex justify-between mb-2 text-xs font-mono-metric text-slate-500">
            <span>Step {step} of {TOTAL}</span>
            <span>{Math.round(progress)}%</span>
          </div>
          <div className="h-1.5 rounded-full bg-[#E5E0D8] overflow-hidden">
            <div className="h-full bg-[#D97706] transition-all duration-500" style={{ width: `${progress}%` }} data-testid="funnel-progress" />
          </div>
        </div>

        <div className="card-warm p-6 md:p-10">
          {step === 1 && (
            <div className="space-y-5" data-testid="funnel-step-1">
              <h2 className="font-serif-display text-2xl text-[#0F382C]">Your contact & property</h2>
              <Field label="Full name" error={errors.full_name}>
                <input data-testid="funnel-full-name" className="funnel-input" value={data.full_name} onChange={(e) => set("full_name", e.target.value)} placeholder="Jane Doe" />
              </Field>
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Mobile phone" error={errors.mobile_phone}>
                  <input data-testid="funnel-phone" className="funnel-input" value={data.mobile_phone} onChange={(e) => set("mobile_phone", e.target.value)} placeholder="(540) 555-0199" />
                </Field>
                <Field label="Email" error={errors.email}>
                  <input data-testid="funnel-email" type="email" className="funnel-input" value={data.email} onChange={(e) => set("email", e.target.value)} placeholder="jane@example.com" />
                </Field>
              </div>
              <Field label="Property address" error={errors.property_address}>
                <input data-testid="funnel-address" className="funnel-input" value={data.property_address} onChange={(e) => set("property_address", e.target.value)} placeholder="1234 Colonial Way, Fredericksburg, VA" />
              </Field>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-5" data-testid="funnel-step-2">
              <h2 className="font-serif-display text-2xl text-[#0F382C]">Your utility & usage</h2>
              <Field label="Utility provider" error={errors.utility_provider}>
                <div className="grid gap-2">
                  {UTILITIES.map((u) => (
                    <label key={u.value} className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition ${data.utility_provider === u.value ? "border-[#D97706] bg-[#FEF3C7]/40" : "border-[#E5E0D8] hover:border-[#D97706]/50"}`} data-testid={`funnel-utility-${u.value}`}>
                      <input type="radio" name="utility" className="accent-[#D97706]" checked={data.utility_provider === u.value} onChange={() => set("utility_provider", u.value)} />
                      <span className="text-sm">{u.label}</span>
                    </label>
                  ))}
                </div>
              </Field>
              <Field label="Average monthly electric bill" error={errors.monthly_bill}>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                  {BILL_BUCKETS.map((b) => (
                    <button type="button" key={b} onClick={() => set("monthly_bill", b)} className={`px-3 py-3 rounded-xl border text-sm ${data.monthly_bill === b ? "bg-[#0F382C] text-[#FDFBF7] border-[#0F382C]" : "bg-white border-[#E5E0D8] hover:border-[#0F382C]/40"}`} data-testid={`funnel-bill-${b}`}>{b}</button>
                  ))}
                </div>
              </Field>
              <YesNo label="Do you own the home?" value={data.owns_home} onChange={(v) => set("owns_home", v)} testId="funnel-owns-home" error={errors.owns_home} />
            </div>
          )}

          {step === 3 && (
            <div className="space-y-5" data-testid="funnel-step-3">
              <h2 className="font-serif-display text-2xl text-[#0F382C]">Your roof & home plans</h2>
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Roof material" error={errors.roof_material}>
                  <select data-testid="funnel-roof-material" className="funnel-input" value={data.roof_material} onChange={(e) => set("roof_material", e.target.value)}>
                    <option value="">Choose…</option>
                    {ROOF_MATERIALS.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                </Field>
                <Field label="Approximate roof age" error={errors.roof_age}>
                  <select data-testid="funnel-roof-age" className="funnel-input" value={data.roof_age} onChange={(e) => set("roof_age", e.target.value)}>
                    <option value="">Choose…</option>
                    {ROOF_AGES.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                </Field>
              </div>
              <YesNo label="Does the property have an HOA?" value={data.has_hoa} onChange={(v) => set("has_hoa", v)} testId="funnel-has-hoa" error={errors.has_hoa} />
              <YesNo label="Do you expect to move soon?" value={data.moving_soon} onChange={(v) => set("moving_soon", v)} testId="funnel-moving-soon" error={errors.moving_soon} />
            </div>
          )}

          {step === 4 && (
            <div className="space-y-5" data-testid="funnel-step-4">
              <h2 className="font-serif-display text-2xl text-[#0F382C]">Schedule & optional bill upload</h2>
              <Field label="Preferred consultation date & time">
                <input data-testid="funnel-datetime" type="datetime-local" className="funnel-input" value={data.preferred_datetime} onChange={(e) => set("preferred_datetime", e.target.value)} />
              </Field>
              <Field label="Utility bill upload (optional, up to 8MB)">
                <input ref={fileRef} type="file" className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} accept="image/*,application/pdf" data-testid="funnel-bill-file" />
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  className={`w-full flex items-center gap-3 p-4 rounded-xl border-2 border-dashed ${data.bill_upload_filename ? "border-[#0F382C] bg-[#E8F0EC]" : "border-[#E5E0D8] hover:border-[#D97706]"} transition`}
                  data-testid="funnel-bill-upload-btn"
                >
                  {uploading ? <Loader2 className="w-5 h-5 animate-spin text-[#D97706]" /> :
                    data.bill_upload_filename ? <FileText className="w-5 h-5 text-[#0F382C]" /> :
                    <Upload className="w-5 h-5 text-[#D97706]" />}
                  <div className="text-left">
                    <div className="font-medium text-[#0F382C]">
                      {data.bill_upload_filename ? data.bill_upload_filename : uploading ? "Uploading…" : "Click to upload your latest bill"}
                    </div>
                    <div className="text-xs text-slate-500">PDF, JPG, or PNG. Speeds up your consultation.</div>
                  </div>
                </button>
              </Field>
            </div>
          )}

          {step === 5 && (
            <div className="space-y-5" data-testid="funnel-step-5">
              <h2 className="font-serif-display text-2xl text-[#0F382C]">Review & consent</h2>
              <div className="rounded-2xl bg-[#F7F4EB] p-5 grid sm:grid-cols-2 gap-3 text-sm">
                <Summary label="Name" value={data.full_name} />
                <Summary label="Phone" value={data.mobile_phone} />
                <Summary label="Email" value={data.email} />
                <Summary label="Address" value={data.property_address} />
                <Summary label="Utility" value={UTILITIES.find((u) => u.value === data.utility_provider)?.label} />
                <Summary label="Monthly bill" value={data.monthly_bill} />
                <Summary label="Owns home" value={data.owns_home ? "Yes" : "No"} />
                <Summary label="Roof" value={`${data.roof_material} · ${data.roof_age}`} />
                <Summary label="HOA" value={data.has_hoa ? "Yes" : "No"} />
                <Summary label="Moving soon" value={data.moving_soon ? "Yes" : "No"} />
                {data.preferred_datetime && <Summary label="Appointment" value={new Date(data.preferred_datetime).toLocaleString()} />}
                {data.bill_upload_filename && <Summary label="Bill uploaded" value={data.bill_upload_filename} />}
              </div>

              <label className="flex items-start gap-3 p-4 rounded-xl border border-[#E5E0D8] cursor-pointer" data-testid="funnel-consent">
                <input type="checkbox" className="mt-1 accent-[#D97706]" checked={data.consent_contact} onChange={(e) => set("consent_contact", e.target.checked)} />
                <span className="text-sm text-slate-600">
                  I consent to receive calls, texts, and emails from ION Solar about my inquiry — including automated messages. Message and data rates may apply. Consent isn't a condition of purchase. This site does not collect your Social Security number. If a soft credit check is required, it will be initiated only through a compliant third-party provider after your explicit authorization.
                </span>
              </label>
              {errors.consent_contact && <div className="text-xs text-red-600" data-testid="funnel-consent-error">{errors.consent_contact}</div>}
            </div>
          )}

          <div className="mt-8 flex items-center justify-between">
            <button
              onClick={back}
              disabled={step === 1}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm text-slate-600 hover:bg-[#F7F4EB] disabled:opacity-30"
              data-testid="funnel-back-btn"
            >
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
            {step < TOTAL ? (
              <button onClick={next} className="btn-solar text-sm" data-testid="funnel-next-btn">
                Continue <ArrowRight className="w-4 h-4" />
              </button>
            ) : (
              <button onClick={submit} disabled={submitting} className="btn-solar text-sm" data-testid="funnel-submit-btn">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Submit qualification
              </button>
            )}
          </div>
        </div>
      </div>

      <style>{`
        .funnel-input {
          width: 100%;
          padding: 0.75rem 1rem;
          border-radius: 0.75rem;
          border: 1px solid #E5E0D8;
          background: #FFFFFF;
          font-size: 0.95rem;
          color: #1E293B;
          transition: border-color 0.2s, box-shadow 0.2s;
        }
        .funnel-input:focus { outline: none; border-color: #D97706; box-shadow: 0 0 0 3px rgba(217,119,6,0.15); }
      `}</style>
    </section>
  );
}

const Field = ({ label, error, children }) => (
  <div>
    <label className="block text-xs font-mono-metric uppercase tracking-widest text-slate-500 mb-2">{label}</label>
    {children}
    {error && <div className="mt-1.5 text-xs text-red-600">{error}</div>}
  </div>
);

const YesNo = ({ label, value, onChange, testId, error }) => (
  <div>
    <label className="block text-xs font-mono-metric uppercase tracking-widest text-slate-500 mb-2">{label}</label>
    <div className="flex gap-2">
      {[
        { v: true, l: "Yes" },
        { v: false, l: "No" },
      ].map((o) => (
        <button
          key={o.l}
          type="button"
          onClick={() => onChange(o.v)}
          className={`flex-1 px-4 py-3 rounded-xl border text-sm font-medium ${value === o.v ? "bg-[#0F382C] text-[#FDFBF7] border-[#0F382C]" : "bg-white border-[#E5E0D8] hover:border-[#0F382C]/40"}`}
          data-testid={`${testId}-${o.l.toLowerCase()}`}
        >
          {o.l}
        </button>
      ))}
    </div>
    {error && <div className="mt-1.5 text-xs text-red-600">{error}</div>}
  </div>
);

const Summary = ({ label, value }) => (
  <div>
    <div className="text-[10px] uppercase tracking-widest font-mono-metric text-slate-500">{label}</div>
    <div className="font-medium text-[#0F382C]">{value || "—"}</div>
  </div>
);
